// Copyright (C) 2019-2022 Intel Corporation
// Copyright (C) 2022-2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Empty from 'antd/lib/empty';
import message from 'antd/lib/message';
import Pagination from 'antd/lib/pagination';
import Result from 'antd/lib/result';
import Text from 'antd/lib/typography/Text';
import copy from 'copy-to-clipboard';
import {
    CopyOutlined, FolderOutlined, MoreOutlined, PlusOutlined, SettingOutlined,
} from '@ant-design/icons';

import {
    getCore, Project, Reference, Task,
} from 'cvat-core-wrapper';
import { CombinedState } from 'reducers';
import { getProjectTasksAsync } from 'actions/projects-actions';
import MoveTaskModal from 'components/move-task-modal/move-task-modal';
import ModelRunnerDialog from 'components/model-runner-modal/model-runner-dialog';
import ReferenceModelRunnerDialog from 'components/model-runner-modal/model-runner-dialog-ref';
import MoveReferenceModal from 'components/move-reference-modal/move-reference-modal';
import ProjectActionsMenu from 'components/projects-page/actions-menu';
import { RowSkeletons, SkeletonLine } from 'components/common/skeletons';
import { plural, t } from 'utils/i18n';
import NewTaskModal from './new-task-modal';
import ReferenceRow from './reference-row';
import TaskRow, { taskPath } from './task-row';

const core = getCore();
const PAGE_SIZE = 10;

export interface ProjectFolder {
    id: number;
    path: string;
    windows_path: string | null;
}

export async function fetchProjectFolder(projectId: number): Promise<ProjectFolder | null> {
    const response = await core.server.request(`${core.config.backendAPI}/projects/${projectId}/project_source`, {
        method: 'GET',
    });
    const [first] = (response.data as ProjectFolder[]) ?? [];
    return first ?? null;
}

// "4 tasks · 2 references" under the project's name
export function projectCounts(tasks: number, references: number): string {
    return `${plural(tasks, 'task')} · ${plural(references, 'reference')}`;
}

export default function ProjectPageComponent(): JSX.Element {
    const id = +useParams<{ id: string }>().id;
    const dispatch = useDispatch();
    const history = useHistory();
    // the list already has the project when the user comes from it: the heading shows at once
    const listed = useSelector((state: CombinedState) => (
        state.projects.current.find((item: Project) => item.id === id)
    ));
    const [project, setProject] = useState<Project | null>(listed ?? null);
    // undefined until the server answered, null when the project has no folder
    const [folder, setFolder] = useState<ProjectFolder | null | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const deletes = useSelector((state: CombinedState) => state.projects.activities.deletes);
    const tasks = useSelector((state: CombinedState) => state.tasks.current);
    const tasksCount = useSelector((state: CombinedState) => state.tasks.count);
    const tasksFetching = useSelector((state: CombinedState) => state.tasks.fetching);
    const tasksQuery = useSelector((state: CombinedState) => state.projects.tasksGettingQuery);
    const references = useSelector((state: CombinedState) => state.references.current);
    const referencesCount = useSelector((state: CombinedState) => state.references.count);
    const referencesFetching = useSelector((state: CombinedState) => state.references.fetching);
    const referencesQuery = useSelector((state: CombinedState) => state.projects.referencesGettingQuery);

    useEffect(() => {
        let cancelled = false;
        setLoading(!listed);
        const fresh = {
            projectId: id, page: 1, search: null as string | null, filter: null as string | null, sort: '-updated_date',
        };
        dispatch(getProjectTasksAsync(fresh, fresh));
        core.projects.get({ id })
            .then(([loaded]: Project[]) => { if (!cancelled && loaded) setProject(loaded); })
            .catch(() => undefined)
            .finally(() => { if (!cancelled) setLoading(false); });
        // the folder is shown as the Windows path the team opens; it is not part of the project itself
        fetchProjectFolder(id).then((value) => { if (!cancelled) setFolder(value); }).catch(() => undefined);
        return () => { cancelled = true; };
    }, [id]);

    useEffect(() => {
        if (project && deletes[id]) history.push('/projects');
    }, [deletes]);

    if (!loading && !project) {
        return (
            <Result
                className='cvat-not-found'
                status='404'
                title={t('There was something wrong during getting the project')}
                subTitle={t('Please, be sure, that information you tried to get exist and you are eligible to access it')}
            />
        );
    }

    const shownPath = folder ? folder.windows_path ?? folder.path : null;
    const copyPath = (): void => {
        if (shownPath && copy(shownPath)) message.success(t('Path copied'));
        else message.error(t('Could not copy the path'));
    };

    // the store's counts belong to this project only once its fetch is done; until then the project knows them
    const tasksLoaded = tasksQuery.projectId === id && !tasksFetching;
    const referencesLoaded = referencesQuery.projectId === id && !referencesFetching;
    const taskTotal = tasksLoaded ? tasksCount : project?.taskCount ?? null;
    const referenceTotal = referencesLoaded ? referencesCount : project?.referenceCount ?? null;

    const projectTasks = tasks.filter((task: Task) => task.projectId === id);
    const taskNames = Object.fromEntries(projectTasks.map((task: Task) => [task.id, task.name]));
    let list: React.ReactNode;
    if (!tasksLoaded && !projectTasks.length && taskTotal !== 0) {
        list = <RowSkeletons count={Math.min(PAGE_SIZE, taskTotal ?? 3)} />;
    } else if (!projectTasks.length) list = <Empty description={t('No tasks yet. Add drawings with New task.')} />;
    else {
        list = (
            <div className='cvat-project-task-list'>
                {projectTasks.map((task: Task) => <TaskRow key={task.id} task={task} />)}
            </div>
        );
    }
    const projectReferences = references.filter((reference: Reference) => reference.projectId === id);
    let referenceList: React.ReactNode = null;
    if (!referencesLoaded && !projectReferences.length && referenceTotal !== 0) {
        referenceList = <RowSkeletons count={Math.min(PAGE_SIZE, referenceTotal ?? 1)} />;
    } else if (projectReferences.length) {
        referenceList = (
            <div className='cvat-project-task-list'>
                {projectReferences.map((reference: Reference) => (
                    <ReferenceRow key={reference.id} reference={reference} taskNames={taskNames} />
                ))}
            </div>
        );
    }

    return (
        <div className='cvat-project-page'>
            <div className='cvat-project-page-content'>
                <div className='cvat-project-heading'>
                    <div className='cvat-project-heading-text'>
                        <h1>{project ? project.name : <SkeletonLine width={220} />}</h1>
                        <Text type='secondary'>
                            {taskTotal !== null && referenceTotal !== null ?
                                projectCounts(taskTotal, referenceTotal) :
                                <SkeletonLine width={160} />}
                        </Text>
                        <div className='cvat-project-folder'>
                            <FolderOutlined />
                            {folder === undefined && <SkeletonLine width={240} />}
                            {shownPath && (
                                <>
                                    <Text className='cvat-project-folder-path' title={shownPath}>{shownPath}</Text>
                                    <Button size='small' icon={<CopyOutlined />} onClick={copyPath}>{t('Copy path')}</Button>
                                </>
                            )}
                            {folder === null && (
                                <>
                                    <Text type='secondary'>{t('No folder yet')}</Text>
                                    <Button type='link' size='small' onClick={() => history.push(`/projects/${id}/settings`)}>
                                        {t('Choose folder')}
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                    <Button type='primary' icon={<PlusOutlined />} onClick={() => setCreating(true)}>
                        {t('New task')}
                    </Button>
                    <Button
                        type='text'
                        aria-label={t('Settings')}
                        icon={<SettingOutlined />}
                        onClick={() => history.push(`/projects/${id}/settings`)}
                    />
                    {project ? (
                        <Dropdown destroyPopupOnHide trigger={['click']} overlay={<ProjectActionsMenu projectInstance={project} />}>
                            <Button type='text' aria-label={t('More')} icon={<MoreOutlined />} />
                        </Dropdown>
                    ) : <Button type='text' aria-label={t('More')} icon={<MoreOutlined />} disabled />}
                </div>
                <section aria-label={t('Tasks')} className='cvat-project-section'>
                    <h2>{t('Tasks')}</h2>
                    {list}
                    {tasksCount > PAGE_SIZE && (
                        <Pagination
                            className='cvat-project-tasks-pagination'
                            onChange={(page) => dispatch(getProjectTasksAsync({ ...tasksQuery, projectId: id, page }))}
                            showSizeChanger={false}
                            total={tasksCount}
                            pageSize={PAGE_SIZE}
                            current={tasksQuery.page}
                        />
                    )}
                </section>
                {/* shown once the number of tasks is known: a guessed tasks skeleton above it would move it */}
                {taskTotal !== null && (referenceList || (referenceTotal ?? 0) > 0) && (
                    <section aria-label={t('References')} className='cvat-project-section'>
                        <h2>{t('References')}</h2>
                        {referenceList}
                        {referencesCount > PAGE_SIZE && (
                            <Pagination
                                className='cvat-project-tasks-pagination'
                                onChange={(page) => dispatch(getProjectTasksAsync(
                                    { ...tasksQuery, projectId: id }, { ...referencesQuery, projectId: id, page },
                                ))}
                                showSizeChanger={false}
                                total={referencesCount}
                                pageSize={PAGE_SIZE}
                                current={referencesQuery.page}
                            />
                        )}
                    </section>
                )}
            </div>
            <MoveTaskModal />
            <MoveReferenceModal />
            <ModelRunnerDialog />
            <ReferenceModelRunnerDialog />
            {creating && (
                <NewTaskModal
                    projectId={id}
                    onClose={() => setCreating(false)}
                    onCreated={(task: Task) => history.push(taskPath(task))}
                />
            )}
        </div>
    );
}
